import { useEffect, lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { Toaster, Skeleton } from '@vitness/ui';
import { SignInRoute } from './routes/SignInRoute';
import { setAuthTokenGetter } from './lib/api';
import { VudgetShell } from './shell/VudgetShell';
import { useStore } from './lib/store';
import { usePreferenceSync } from './lib/usePreferences';
import { useMasked } from './lib/useMasked';
import { DashboardRoute } from './routes/DashboardRoute';
import { SettingsRoute } from './routes/SettingsRoute';

// Split the heavy routes. The Dashboard needs Chart.js for its donut, so that
// chunk is on the critical path either way — but TanStack Table (Expenses) and
// the projection UI aren't, so they load on demand.
const ProjectionsRoute = lazy(() => import('./routes/ProjectionsRoute').then(m => ({ default: m.ProjectionsRoute })));
const NetWorthRoute    = lazy(() => import('./routes/NetWorthRoute').then(m => ({ default: m.NetWorthRoute })));
const ExpensesRoute    = lazy(() => import('./routes/ExpensesRoute').then(m => ({ default: m.ExpensesRoute })));
const FundingRoute     = lazy(() => import('./routes/FundingRoute').then(m => ({ default: m.FundingRoute })));

function RouteFallback() {
  return (
    <>
      <Skeleton style={{ height: 52, marginBottom: 22 }} />
      <Skeleton style={{ height: 360 }} />
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/*
          Splat, because Clerk's SignIn is path-routed: it appends its own steps
          (/sign-in/factor-one, /sign-in/sso-callback) and a bare "/sign-in"
          route would 404 the second factor.
        */}
        <Route path="/sign-in/*" element={<SignInRoute />} />
        <Route path="*" element={<RequireAuth><AuthedApp /></RequireAuth>} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

/**
 * Gates everything that isn't the sign-in screen.
 *
 * The same shape as the showcase's `RequireAuth`. Holding render until
 * `isLoaded` matters: Clerk resolves the session from a cookie asynchronously,
 * so redirecting on `!isSignedIn` before then bounces a signed-in user to the
 * sign-in screen on every reload.
 *
 * It also gates the data layer. `AuthedApp` is where `store.load()` lives, so
 * `/api/bootstrap` is never fired unauthenticated and the store never has to
 * model a 401.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div role="status" aria-label="Loading" style={{ padding: 24 }}>
        <Skeleton style={{ height: 52, marginBottom: 22 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;

  return <>{children}</>;
}

function AuthedApp() {
  const load = useStore(s => s.load);
  useEffect(() => { load(); }, [load]);
  usePreferenceSync();
  // Subscribing HERE is what makes the privacy mask work: the route elements
  // are created in this render, so a toggle re-creates them and every
  // fmtMoney call in the tree re-runs against the new flag. Subscribing only
  // in the shell would re-render the shell around identical child elements,
  // which React skips.
  useMasked();

  return (
    <>
      <TokenBridge />
      <VudgetShell>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/"            element={<DashboardRoute />} />
            <Route path="/projections" element={<ProjectionsRoute />} />
            {/* Scenarios is a Projections sub-view; the path stays linkable. */}
            <Route path="/scenarios"   element={<ProjectionsRoute />} />
            <Route path="/expenses"    element={<ExpensesRoute />} />
            <Route path="/networth"    element={<NetWorthRoute />} />
            <Route path="/funding"     element={<FundingRoute />} />
            {/* Mapping is Funding's configuration, not a preference — same
                component, view derived from the path. */}
            <Route path="/funding/allocation" element={<FundingRoute />} />
            <Route path="/funding/settings" element={<FundingRoute />} />
            <Route path="/settings"    element={<SettingsRoute />} />
            {/* Sync folded into Settings. */}
            <Route path="/sync"        element={<Navigate to="/settings" replace />} />
            <Route path="*"            element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </VudgetShell>
    </>
  );
}

/**
 * Hands `api` a way to mint session tokens.
 *
 * Renders nothing — it exists because `api` is a module and `useAuth` is a hook,
 * and this is the one place inside the provider where the two can be introduced.
 * Mounted above the routes so the getter is registered before any route effect
 * fires a request.
 */
function TokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  return null;
}
